import React from 'react';
import styled from 'styled-components';
import { Color } from 'core';
import { FormContainer, FormGroup, FormLabel, FormInput, FormSelect, FormPrimaryButton, ButtonContainer } from './FormComponents';

const DIFFICULTY_LEVELS = ['Beginner', 'Intermediate', 'Advanced'] as const;
export type DifficultyLevel = typeof DIFFICULTY_LEVELS[number];

interface SmartCreateFormProps {
  topic: string;
  difficulty: DifficultyLevel;
  pdfFile?: File | null;
  onTopicChange: (value: string) => void;
  onDifficultyChange: (value: DifficultyLevel) => void;
  onFileUpload?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  isCreating: boolean;
  submitButtonText: string;
  topicPlaceholder?: string;
}


export const SmartCreateForm = ({
  topic,
  difficulty,
  pdfFile,
  onTopicChange,
  onDifficultyChange,
  onFileUpload,
  onSubmit,
  isCreating,
  submitButtonText,
  topicPlaceholder = "E.g. Introduction to Machine Learning"
}: SmartCreateFormProps) => {
  const isFormValid = topic.trim().length > 0;

  const handleUploadClick = () => {
    const fileInput = document.getElementById('pdf-upload') as HTMLInputElement;
    fileInput?.click();
  };

  return (
    <>
      <FormContainer>
        <FormGroup>
          <FormLabel required>Topic</FormLabel>
          <FormInput
            value={topic}
            onChange={(e) => onTopicChange(e.target.value)}
            placeholder={topicPlaceholder}
          />
        </FormGroup>

        <FormGroup>
          <FormLabel>Difficulty</FormLabel>
          <FormSelect
            value={difficulty}
            onChange={(e) => onDifficultyChange(e.target.value as DifficultyLevel)}
            options={[...DIFFICULTY_LEVELS]}
          />
        </FormGroup>

        {onFileUpload && (
          <FormGroup>
            <FormLabel>Upload PDF (optional)</FormLabel>
            <UploadArea onClick={handleUploadClick}>
              <UploadText>
                {pdfFile ? pdfFile.name : 'Click to upload a PDF'}
              </UploadText>
              <HiddenFileInput
                id="pdf-upload"
                type="file"
                accept=".pdf"
                onChange={onFileUpload}
              />
            </UploadArea>
          </FormGroup>
        )}
      </FormContainer>

      <ButtonContainer>
        <FormPrimaryButton onClick={onSubmit} disabled={!isFormValid || isCreating}>
          {isCreating ? 'Creating...' : submitButtonText}
        </FormPrimaryButton>
      </ButtonContainer>
    </>
  );
};

const UploadArea = styled.div`
  border: 2px dashed #767676;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:hover {
    border-color: ${Color.primaryBlue};
    background-color: #f8f9ff;
  }
`;

const UploadText = styled.p`
  color: ${Color.primaryBlue};
  font-size: 16px;
  margin: 0;
`;

const HiddenFileInput = styled.input`
  display: none;
`;